import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useHistorischBesluit } from '@/api/queries/historischBesluit';
import { BesluitCard } from '@/components/BesluitCard/BesluitCard';
import { Button } from '@/components/Button/Button';
import { CardHorizontal } from '@/components/CardHorizontal/CardHorizontal';
import { ModalTrigger } from '@/components/Modal';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { RoleBasedAccess } from '@/components/RoleBasedAccess';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import {
  RegularFileImport,
  RegularPenLine,
  RegularTrash,
} from '@/icons/components';
import { HistorischBesluitDelete } from '@/modals/HistorischBesluitDelete';
import { HistorischBesluitEigenschappen } from '@/modals/HistorischBesluitEigenschappen';
import { DateUtils } from '@/utils/dateUtils';

const searchSchema = z.object({
  p: z.number().optional().default(1),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const historischBesluitOptions = (lineageId: string, search: SearchParams) => {
  return useHistorischBesluit.getOptions(
    {
      page: search.p,
      size: 10,
      filter: [
        {
          model: 'HistorischBesluit',
          field: 'Bedrijf_lineage',
          op: 'eq',
          value: lineageId,
        },
      ],
      sort: [
        {
          model: 'HistorischBesluit',
          field: 'Besluit_Nummer',
          direction: 'asc',
        },
      ],
    },
    {
      placeholderData: keepPreviousData,
    }
  );
};

const latestBedrijfQueryOptions = (lineageId: string) =>
  useLatestBedrijf.getOptions(lineageId);

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/bedrijf/_layout/historische-besluiten'
)({
  validateSearch: searchSchema.parse,
  beforeLoad: () => ({
    getTitle: () => 'Historische besluiten',
  }),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ params, context: { queryClient }, deps }) => {
    const [historischBesluiten] = await Promise.all([
      queryClient.ensureQueryData(
        historischBesluitOptions(params.bedrijfLineageId, deps.search)
      ),
      queryClient.ensureQueryData(
        latestBedrijfQueryOptions(params.bedrijfLineageId)
      ),
    ]);

    if (!historischBesluiten.num_pages) return;

    if (historischBesluiten.num_pages < deps.search.p) {
      throw redirect({
        to: '/$bedrijfLineageId/bedrijf/historische-besluiten',
        search: { p: historischBesluiten.num_pages },
        params,
      });
    }
  },
  component: HistorischeBesluitenComponent,
});

/**
 * Route component
 */
function HistorischeBesluitenComponent() {
  const { bedrijfLineageId } = Route.useParams();
  const search = Route.useSearch();

  const bedrijf = useQuery(latestBedrijfQueryOptions(bedrijfLineageId));
  const historischBesluiten = useQuery(
    historischBesluitOptions(bedrijfLineageId, search)
  );

  if (!bedrijf.data) return null;

  return (
    <>
      <RoleBasedAccess
        allowed={['Vergunningverlener']}
        area={['omgevingsdienst']}
      >
        <section className="border-b border-borderline-light py-5">
          <CardHorizontal>
            <CardHorizontal.Header>
              Nieuw historisch besluit
            </CardHorizontal.Header>

            <ModalTrigger
              modalComponent={HistorischBesluitEigenschappen}
              onOpen={() => ({ bedrijf: bedrijf.data! })}
              asChild
            >
              <CardHorizontal.Button data-testid="besluit-card-new-historisch-besluit">
                Aanmaken
              </CardHorizontal.Button>
            </ModalTrigger>
          </CardHorizontal>
        </section>
      </RoleBasedAccess>

      {historischBesluiten.data ? (
        <section className="py-5">
          <h2 className="style-h5-bold mb-2 text-gray-800">
            Historische besluiten
          </h2>

          {historischBesluiten.data.objects?.length ? (
            <ul className="flex flex-col gap-2">
              {historischBesluiten.data.objects.map((historischBesluit) => (
                <li key={`historisch-besluit-card-${historischBesluit.ID!}`}>
                  <BesluitCard icon={RegularFileImport}>
                    <BesluitCard.Body>
                      <BesluitCard.Heading
                        title={historischBesluit.Besluit_Nummer}
                        headingAs="h3"
                        subtitle={
                          historischBesluit.Besluit_Datum
                            ? `Besluit genomen op ${DateUtils.formatReadable(
                                new Date(historischBesluit.Besluit_Datum)
                              )}`
                            : undefined
                        }
                      />

                      {historischBesluit.Besluit_Omschrijving && (
                        <BesluitCard.Description>
                          {historischBesluit.Besluit_Omschrijving}
                        </BesluitCard.Description>
                      )}

                      <RoleBasedAccess
                        allowed={['Vergunningverlener']}
                        area={['omgevingsdienst']}
                      >
                        <BesluitCard.ButtonRow>
                          <div className="flex w-full justify-between">
                            <ModalTrigger
                              modalComponent={HistorischBesluitEigenschappen}
                              onOpen={() => ({
                                bedrijf: bedrijf.data!,
                                historischBesluitToUpdate: historischBesluit,
                              })}
                              asChild
                            >
                              <Button icon={RegularPenLine} variant="secondary">
                                Bewerken
                              </Button>
                            </ModalTrigger>

                            <Tooltip description="Verwijderen">
                              <div>
                                <ModalTrigger
                                  asChild
                                  modalComponent={HistorischBesluitDelete}
                                  onOpen={() => ({
                                    id: historischBesluit.ID ?? '',
                                    besluitNummer:
                                      historischBesluit.Besluit_Nummer,
                                  })}
                                >
                                  <Button
                                    icon={RegularTrash}
                                    size="icon"
                                    variant="tertiary"
                                    data-testid="historisch-besluit-card-verwijderen"
                                  >
                                    <span className="sr-only">Verwijderen</span>
                                  </Button>
                                </ModalTrigger>
                              </div>
                            </Tooltip>
                          </div>
                        </BesluitCard.ButtonRow>
                      </RoleBasedAccess>
                    </BesluitCard.Body>
                  </BesluitCard>
                </li>
              ))}
            </ul>
          ) : (
            <p className="style-body italic text-placeholder">
              Er zijn geen historische besluiten voor dit bedrijf
            </p>
          )}

          {(historischBesluiten.data?.num_pages ?? 0) > 1 ? (
            <div className="mt-5 flex justify-center">
              <PaginationLinks
                aria-label="Historische besluiten pagina's"
                renderLink={({ children, page }) => (
                  <Link
                    to="/$bedrijfLineageId/bedrijf/historische-besluiten"
                    params={{ bedrijfLineageId }}
                    search={{ p: page }}
                  >
                    {children}
                  </Link>
                )}
                currentPage={search.p}
                totalPages={historischBesluiten.data?.num_pages ?? 0}
              />
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
